<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { transform } from 'lodash-es';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';
import { VueDraggable } from 'vue-draggable-plus';

import type { IGroupSortableOptions } from '@modules/reports/types';
import VDropdownMenu from '@components/dropdowns/VDropdownMenu/VDropdownMenu.vue';
import VLoader from '@components/loader/VLoader.vue';
import VFormFilterButton from '@modules/form-widgets/components/VFormFilterButton/VFormFilterButton.vue';
import { useGroupBySelectOptions } from '@modules/reports/composables';
import { EGroupNames } from '@modules/reports/types';
import { watchImmediate } from '@vueuse/core';

import type {
  IGroupBySortableEmits,
  IGroupBySortableProps,
  IGroupSelectSortableOptions,
} from './types';

const props = defineProps<IGroupBySortableProps>();
const emit = defineEmits<IGroupBySortableEmits>();

const SHORTCODE_PART = 'REPORTS.GROUP_BY.';

const { i18next } = useTranslation();
const { buildList } = useGroupBySelectOptions();

const groupByValues = ref<IGroupSelectSortableOptions>({
  [EGroupNames.AFFILIATE]: [],
});

const groupByOptions = computed<IGroupSelectSortableOptions>(() => ({
  [EGroupNames.AFFILIATE]: buildList(
    props.groupOptions?.[EGroupNames.AFFILIATE],
    props.translationKey,
    `${SHORTCODE_PART}%s`,
  ).filter(({ value }) => !groupByValues.value?.[EGroupNames.AFFILIATE].find((item) => item.value === value)),
}));

watchImmediate(() => props.groupValues, (newValues) => {
  updateValuesList(newValues);
}, { deep: true });

onMounted(() => {
  i18next.on('localeChanged', updateValuesList);
});

onUnmounted(() => {
  i18next.off('localeChanged', updateValuesList);
});

function updateValuesList(newList?: IGroupSortableOptions): void {
  const list: IGroupSortableOptions = newList ?? transform(groupByValues.value, (acc, values, key) => {
    Object.assign(acc, {
      [key]: values.map(({ value }) => value),
    });
    return acc;
  }, {} as IGroupSortableOptions);

  for (const [key, values] of Object.entries(list)) {
    groupByValues.value[key as keyof IGroupSortableOptions] = buildList(
      values,
      props.translationKey,
      `${SHORTCODE_PART}%s`,
    );
  }
}

function onChange(key: keyof IGroupSortableOptions, value: string): void {
  const option = groupByOptions.value[key].find((item) => item.value === value);

  if (option) {
    groupByValues.value[key].push(option);
  }

  emit('change', key, groupByValues.value[key].map((item) => item.value));
}

function onRemove(key: keyof IGroupSortableOptions, value: string): void {
  groupByValues.value[key] = groupByValues.value[key].filter((item) => item.value !== value);
  emit('change', key, groupByValues.value[key].map((item) => item.value));
}

function onEnd(key: keyof IGroupSortableOptions): void {
  emit('change', key, groupByValues.value[key].map((item) => item.value));
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'VGroupBySortable'"
    :loading="isLoading"
    mode="overlay"
    class="min-h-11"
    spinner-size="small"
    spinner-wrapper-class="w-full"
  >
    <template
      v-for="(groupValue, groupKey) in groupByOptions"
      :key="groupKey"
    >
      <div
        class="mt-3 flex min-h-11 items-center gap-2"
      >
        <div class="max-w-60 flex-none text-muted-color word-break">
          {{ settings?.[groupKey]?.label }}
        </div>

        <VueDraggable
          v-model="groupByValues[groupKey]"
          :animation="200"
          :disabled="groupByValues[groupKey].length === 1"
          ghost-class="opacity-50"
          chosen-class="bg-gray-100"
          class="flex flex-wrap gap-2"
          @end="onEnd(groupKey)"
        >
          <VFormFilterButton
            v-for="item in groupByValues[groupKey]"
            :key="item.value"
            type="transparent-secondary"
            :label="item.label"
            :allow-expand="false"
            :allow-remove="settings?.[groupKey]?.required ? groupByValues[groupKey].length > 1 : true"
            class="py-1"
            :class="{ 'cursor-grab': settings?.[groupKey]?.required ? groupByValues[groupKey].length > 1 : true }"
            @click-remove="onRemove(groupKey, item.value)"
          />
        </VueDraggable>

        <VDropdownMenu
          :name="`group_by_${groupKey}`"
          class="min-w-0 flex-none"
          :options="groupValue"
          :disabled="groupValue?.length === 0"
          style-type="dark"
          left-icon="pi-plus"
          :label="$getTranslation(translationKey, 'REPORTS.GROUP_BY.AFFILIATE.ADD_GROUPING_BTN')"
          zero-min-width
          hide-chevron
          @change="(value) => onChange(groupKey, value)"
        />
      </div>
    </template>
  </VLoader>
</template>
